// useTableNavState — Tabellen-Zustand (Filter/Sort/Scroll/loadedCount) über die
// Navigation hinweg erhalten, ABER NUR beim Rücksprung aus einer Detailansicht.
//
// Regel (mit User abgestimmt): Tabelle → Detail → zurück  ⇒  wiederherstellen.
// Frischer Einstieg in die Tabelle von woanders  ⇒  zurücksetzen (leer/oben).
//
// Erkennung: ein global getrackter „letzter Pfad" (von NavigationLayout bei
// jeder Route gesetzt). Beim Mounten der Tabelle wird der zuletzt getrackte Pfad
// (= die Route, von der wir gerade kommen) gelesen; ist das eine Detailroute,
// wird der gespeicherte Snapshot wiederhergestellt, sonst verworfen. „Zurück"
// im Detail ist ein navigate() (PUSH) — daher routebasiert statt nur POP.

import { useCallback, useState } from "react";

const KEY_PREFIX = "kvp.tablestate.";

// Zuletzt vollständig getrackter Pfad (von NavigationLayout per Effect gesetzt).
// Beim Mount der Tabelle steht hier noch die Route, von der wir kommen, weil der
// Layout-Effect für die neue Route erst NACH dem Render der Kinder läuft.
let lastTrackedPath: string | null = null;

export function trackTableRoute(pathname: string): void {
  lastTrackedPath = pathname;
}

export function useTableNavState<S>(
  key: string,
  opts: { isDetailRoute: (path: string) => boolean },
): { initial: S | null; save: (snapshot: S) => void } {
  const storeKey = KEY_PREFIX + key;
  const isDetail = opts.isDetailRoute;

  // Einmal beim ersten Render auswerten (vor dem Layout-Route-Effect).
  const [initial] = useState<S | null>(() => {
    const cameFromDetail = isDetail(lastTrackedPath ?? "");
    let snap: S | null = null;
    try {
      const raw = sessionStorage.getItem(storeKey);
      if (raw) snap = JSON.parse(raw) as S;
    } catch {
      snap = null;
    }
    if (!cameFromDetail) {
      // Frischer Einstieg → gespeicherten Zustand verwerfen.
      try {
        sessionStorage.removeItem(storeKey);
      } catch {
        /* ignore */
      }
      return null;
    }
    return snap;
  });

  const save = useCallback(
    (snapshot: S) => {
      try {
        sessionStorage.setItem(storeKey, JSON.stringify(snapshot));
      } catch {
        /* sessionStorage voll/blockiert — Wiederherstellung ist best-effort */
      }
    },
    [storeKey],
  );

  return { initial, save };
}
